import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Award,
  Loader2,
  Users,
  Sparkles,
  ChevronDown,
  CheckCircle2,
  XCircle,
  MessageCircle,
  Circle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const STUDENT_ID = 's2a1'; // Carlos Mendoza

// Subject options for the toggle (no icons)
const SUBJECT_OPTIONS = [
  { id: null, name: 'Todas' },
  { id: 'filosofia', name: 'Filosofía' },
  { id: 'historia-arte', name: 'Historia del Arte' },
  { id: 'cultura-audiovisual', name: 'Cultura Audiovisual' }
];

// Subject name mapping
const SUBJECT_NAMES = {
  'filosofia': 'Filosofía',
  'historia-arte': 'Historia del Arte',
  'cultura-audiovisual': 'Cultura Audiovisual'
};

// Progress Summary Component with AI
const ProgressSummary = ({ studentId, subjectId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, [studentId, subjectId]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/student/${studentId}/progress-summary`;
      if (subjectId) {
        url += `?subject_id=${subjectId}`;
      }
      const response = await fetch(url);
      if (!response.ok) throw new Error('Error al cargar resumen');
      const result = await response.json();
      setSummary(result);
    } catch (err) {
      console.error('Error fetching summary:', err);
      setSummary({ summary: 'No se pudo generar el análisis.', has_data: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200" data-testid="progress-summary">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-amber-50 rounded-lg flex-shrink-0">
          <Sparkles className="w-4 h-4 text-amber-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Análisis de tu progreso</h3>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Analizando tus avances...</span>
            </div>
          ) : (
            <p className="text-sm text-gray-600 leading-relaxed">
              {summary?.summary}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Custom Tooltip for Line Chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-100">
        <p className="text-xs text-gray-500 mb-1">{data.topic || label}</p>
        <p className="text-sm font-semibold text-blue-600">Tú: {data.score}%</p>
        <p className="text-sm text-gray-500">Grupo: {data.group_avg}%</p>
      </div>
    );
  }
  return null;
};

// Line Chart Component with 2 lines
const ProgressLineChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  const chartData = data.map((item, idx) => ({
    ...item,
    index: idx + 1,
    name: item.date
  }));

  return (
    <div className="h-48 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={false}
          />
          <YAxis 
            domain={[0, 100]} 
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={false}
            ticks={[0, 25, 50, 75, 100]}
          />
          <Tooltip content={<CustomTooltip />} />
          {/* Student progress line */}
          <Line
            type="monotone"
            dataKey="score"
            name="Tu progreso"
            stroke="#3b82f6"
            strokeWidth={2.5}
            dot={{ r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#fff' }}
            activeDot={{ r: 6, fill: '#2563eb', strokeWidth: 2, stroke: '#fff' }}
          />
          {/* Group average line */}
          <Line
            type="monotone"
            dataKey="group_avg"
            name="Promedio del grupo"
            stroke="#9ca3af"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ r: 3, fill: '#9ca3af', strokeWidth: 1, stroke: '#fff' }}
            activeDot={{ r: 5, fill: '#6b7280', strokeWidth: 2, stroke: '#fff' }}
          />
        </LineChart>
      </ResponsiveContainer>
      {/* Custom Legend */}
      <div className="flex items-center justify-center gap-6 mt-2 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-blue-500 rounded"></div>
          <span className="text-gray-600">Tu progreso</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-gray-400 rounded" style={{ borderStyle: 'dashed' }}></div>
          <span className="text-gray-500">Promedio de tu grupo</span>
        </div>
      </div>
    </div>
  );
};

// Subject Toggle (Horizontal Pills - NO ICONS)
const SubjectToggle = ({ selectedSubject, onSelect }) => {
  return (
    <div className="bg-gray-100 rounded-xl p-1 flex flex-wrap sm:flex-nowrap gap-1" data-testid="subject-toggle">
      {SUBJECT_OPTIONS.map((option) => {
        const isSelected = selectedSubject === option.id;
        
        return (
          <button
            key={option.id || 'todas'}
            onClick={() => onSelect(option.id)}
            className={`flex-1 min-w-[70px] flex items-center justify-center px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
              isSelected 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`subject-toggle-${option.id || 'todas'}`}
          >
            <span className="truncate">{option.name}</span>
          </button>
        );
      })}
    </div>
  );
};

// Question Answer Item - Different display for correct vs incorrect
const QuestionAnswerItem = ({ qa, subjectId, onStudyClick }) => {
  const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
  
  if (qa.is_correct) {
    // CORRECT ANSWER: Just show the correct answer text
    return (
      <div className="p-3 rounded-lg bg-emerald-50">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-700 mb-2">{qa.question}</p>
            <p className="text-xs text-emerald-700">
              <span className="font-medium">✓ Respuesta correcta:</span> {qa.student_answer}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // INCORRECT ANSWER: Show all options with student selection marked
  return (
    <div className="p-3 rounded-lg bg-red-50">
      <div className="flex items-start gap-2">
        <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-700 mb-3">{qa.question}</p>
          
          {/* Show all options if available */}
          {qa.options && qa.options.length > 0 ? (
            <div className="space-y-1.5 mb-3">
              {qa.options.map((option, idx) => {
                const isStudentChoice = idx === qa.student_answer_index;
                const isCorrectAnswer = idx === qa.correct_answer_index;
                
                return (
                  <div 
                    key={idx}
                    className={`flex items-center gap-2 p-2 rounded-md text-xs ${
                      isCorrectAnswer 
                        ? 'bg-emerald-100 border border-emerald-300' 
                        : isStudentChoice 
                          ? 'bg-red-100 border border-red-300'
                          : 'bg-white border border-gray-200'
                    }`}
                  >
                    <span className={`font-medium ${
                      isCorrectAnswer 
                        ? 'text-emerald-600' 
                        : isStudentChoice 
                          ? 'text-red-600'
                          : 'text-gray-500'
                    }`}>
                      {letters[idx]})
                    </span>
                    <span className={`flex-1 ${
                      isCorrectAnswer 
                        ? 'text-emerald-700' 
                        : isStudentChoice 
                          ? 'text-red-700'
                          : 'text-gray-600'
                    }`}>
                      {option}
                    </span>
                    {isStudentChoice && (
                      <span className="text-[10px] font-medium text-red-500 bg-red-200 px-1.5 py-0.5 rounded">
                        Tu respuesta
                      </span>
                    )}
                    {isCorrectAnswer && (
                      <span className="text-[10px] font-medium text-emerald-500 bg-emerald-200 px-1.5 py-0.5 rounded">
                        Correcta
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            // Open question - show text answers
            <div className="space-y-2 mb-3">
              <p className="text-xs text-red-700">
                <span className="font-medium">Tu respuesta:</span> {qa.student_answer || '(sin respuesta)'}
              </p>
              {qa.correct_answer && (
                <p className="text-xs text-emerald-700">
                  <span className="font-medium">Respuesta correcta:</span> {qa.correct_answer}
                </p>
              )}
            </div>
          )}
          
          {/* Study button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onStudyClick(subjectId, qa.question, qa.topic);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors"
            data-testid="study-question-btn"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            <span>Estudiar este tema</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Expandable Challenge Result Row
const ChallengeResultRow = ({ result, onStudyClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 overflow-hidden" 
      data-testid={`result-row-${result.challenge_id}`}
    >
      {/* Clickable Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
        data-testid={`result-row-toggle-${result.challenge_id}`}
      >
        {/* Score */}
        <div className="flex-shrink-0 w-14 text-center">
          <span className="text-lg font-bold text-gray-700">
            {result.correct_count}/{result.total_questions}
          </span>
          <p className="text-[10px] text-gray-400 mt-0.5">aciertos</p>
        </div>
        
        {/* Title and Date */}
        <div className="flex-1 text-left min-w-0">
          <h3 className="font-medium text-gray-900 truncate">{result.topic}</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Calificado el {result.graded_date}
          </p>
        </div>
        
        {/* Expand Icon */}
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Expanded Content - Questions and Answers */}
      {isExpanded && result.questions_answers && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 py-3">Preguntas y respuestas:</p>
          <div className="space-y-2">
            {result.questions_answers.map((qa, idx) => (
              <QuestionAnswerItem 
                key={idx} 
                qa={qa} 
                subjectId={result.subject_id}
                onStudyClick={onStudyClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const StudentResultadosView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);

  useEffect(() => {
    fetchResults();
  }, [selectedSubject]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/student/${STUDENT_ID}/results`;
      if (selectedSubject) {
        url += `?subject_id=${selectedSubject}`;
      }
      const response = await fetch(url);
      if (!response.ok) throw new Error('Error al cargar resultados');
      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching results:', err);
      setError('No se pudieron cargar los resultados');
    } finally {
      setLoading(false);
    }
  };

  const handleStudyClick = (subjectId, question, topic) => {
    // Create an educational message that helps the student learn
    const subjectName = SUBJECT_NAMES[subjectId] || 'esta materia';
    
    // Reformulate the request to be more educational
    const educationalMessage = `Me equivoqué en una pregunta sobre "${topic}". La pregunta era: "${question}". ¿Puedes explicarme este tema de manera clara y ayudarme a entenderlo mejor? Me gustaría aprender los conceptos clave para no volver a equivocarme.`;
    
    const targetSubject = subjectId || 'filosofia';
    
    localStorage.setItem('pendingChatQuestion', JSON.stringify({
      subjectId: targetSubject,
      question: educationalMessage,
      timestamp: Date.now()
    }));
    
    // Navigate to chat
    window.dispatchEvent(new CustomEvent('navigateToChat', { 
      detail: { subjectId: targetSubject, question: educationalMessage }
    }));
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="results-loading">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-500" data-testid="results-error">
        <BarChart3 className="w-12 h-12 mb-4 text-gray-300" />
        <p>{error}</p>
      </div>
    );
  }

  // Show empty state but with toggle visible
  if (!data || data.total_challenges === 0) {
    return (
      <div className="h-full overflow-y-auto" data-testid="results-empty">
        <div className="max-w-2xl mx-auto p-4 space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-xl">
              <BarChart3 className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Mis Resultados</h1>
              <p className="text-sm text-gray-500">0 retos completados</p>
            </div>
          </div>

          {/* Subject Toggle - Always visible */}
          <SubjectToggle 
            selectedSubject={selectedSubject}
            onSelect={setSelectedSubject}
          />

          {/* Empty State Message */}
          <div className="flex flex-col items-center justify-center text-gray-500 py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <BarChart3 className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Sin resultados aún</h3>
            <p className="text-sm text-center max-w-xs">
              {selectedSubject 
                ? "No tienes retos completados en esta asignatura. Prueba con otra o selecciona 'Todas'."
                : "Completa algunos retos para ver tus resultados y estadísticas aquí."
              }
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="student-resultados-view">
      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-xl">
            <BarChart3 className="w-6 h-6 text-blue-500" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Mis Resultados</h1>
            <p className="text-sm text-gray-500">{data.total_challenges} retos completados</p>
          </div>
        </div>

        {/* Subject Toggle */}
        <SubjectToggle 
          selectedSubject={selectedSubject}
          onSelect={setSelectedSubject}
        />

        {/* Overall Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Average Score */}
          <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="stat-average">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-gray-500">Promedio</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{data.average_score}%</p>
          </div>
          
          {/* Best Score */}
          <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="stat-best">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-gray-500">Mejor</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{data.best_score}%</p>
          </div>
          
          {/* Vs Group */}
          <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="stat-vsgroup">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-gray-500">vs Grupo</span>
            </div>
            <div className="flex items-center gap-1">
              {data.group_comparison && data.group_comparison.difference >= 0 ? (
                <>
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  <span className="text-2xl font-bold text-emerald-600">+{data.group_comparison.difference}%</span>
                </>
              ) : data.group_comparison ? (
                <>
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  <span className="text-2xl font-bold text-red-600">{data.group_comparison.difference}%</span>
                </>
              ) : (
                <span className="text-2xl font-bold text-gray-400">--</span>
              )}
            </div>
          </div>
        </div>

        {/* Progress Line Chart with 2 lines */}
        {data.progress_data && data.progress_data.length > 1 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="progress-chart">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Tu progreso</span>
              </div>
              <span className="text-xs text-gray-400">Últimos {data.progress_data.length} retos</span>
            </div>
            <ProgressLineChart data={data.progress_data} />
          </div>
        )}

        {/* AI Progress Summary */}
        <ProgressSummary studentId={STUDENT_ID} subjectId={selectedSubject} />

        {/* Challenge Results List */}
        {data.challenges && data.challenges.length > 0 && (
          <div data-testid="challenge-results-list">
            <h2 className="text-sm font-medium text-gray-700 mb-3">Detalle por reto</h2>
            <div className="space-y-2">
              {data.challenges.map((challenge) => (
                <ChallengeResultRow 
                  key={challenge.challenge_id} 
                  result={challenge}
                  onStudyClick={handleStudyClick}
                />
              ))}
            </div>
          </div>
        )}

        {/* Footer spacing */}
        <div className="h-8"></div>
      </div>
    </div>
  );
};

export default StudentResultadosView;
